import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { OPEN_COMMAND_PALETTE_EVENT } from '@/lib/nav'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Home,
  BarChart3,
  Handshake,
  DollarSign,
  Settings,
  Zap,
  Upload,
  UserPlus,
  Briefcase,
  Truck,
  Calendar,
  Building2,
  ClipboardCheck,
  Radio,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { seatPath } from '@/lib/agent-seats'
import { useToast } from '@/contexts/ToastContext'

interface CommandItem {
  id: string
  label: string
  description?: string
  icon: React.ReactNode
  category: 'page' | 'agent' | 'action'
  onSelect: () => void
  keywords?: string[]
  shortcut?: string
}

const agentDotColors: Record<string, string> = {
  scout: 'bg-blue-400',
  analyst: 'bg-violet-400',
  marketer: 'bg-emerald-400',
  closer: 'bg-amber-400',
  dispo: 'bg-cyan-400',
  operator: 'bg-zinc-300',
}

function AgentDot({ role }: { role: string }) {
  return (
    <span
      className={cn('inline-block w-3 h-3 rounded-full shrink-0', agentDotColors[role])}
    />
  )
}

const categoryLabels: Record<string, string> = {
  page: 'Pages',
  agent: 'Agents',
  action: 'Actions',
}

/** Returns true if the query looks like a street address (digits + letters). */
function looksLikeAddress(q: string): boolean {
  const trimmed = q.trim()
  return trimmed.length > 4 && /\d/.test(trimmed) && /[a-zA-Z]/.test(trimmed)
}

/** Simple case-insensitive fuzzy match: every character in the query appears in order. */
function fuzzyMatch(text: string, query: string): boolean {
  const lower = text.toLowerCase()
  const q = query.toLowerCase()
  let j = 0
  for (let i = 0; i < lower.length && j < q.length; i++) {
    if (lower[i] === q[j]) j++
  }
  return j === q.length
}

export default function CommandPalette() {
  const navigate = useNavigate()
  const { addToast } = useToast()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // -- Build command items (stable across renders via navigate/addToast refs) --
  const allItems = useMemo<CommandItem[]>(() => {
    const pages: CommandItem[] = [
      { id: 'home', label: 'Home', description: 'Morning Briefing', icon: <Home className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/home'), keywords: ['briefing', 'dashboard', 'decisions'], shortcut: '\u23181' },
      { id: 'find', label: 'Find', description: 'Scout & Lead Generation', icon: <Search className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/find'), keywords: ['leads', 'scout', 'lists', 'upload'], shortcut: '\u23182' },
      { id: 'eval', label: 'Eval', description: 'Property Evaluation', icon: <BarChart3 className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/eval'), keywords: ['property', 'comps', 'mao', 'arv', 'analysis'], shortcut: '\u23183' },
      { id: 'opportunities', label: 'Opportunities', description: 'Pipeline Management', icon: <Handshake className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/opportunities'), keywords: ['pipeline', 'offers', 'contracts', 'closer', 'deals'], shortcut: '\u23184' },
      { id: 'schedule', label: 'Schedule', description: 'Calendar & appointments', icon: <Calendar className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/schedule'), keywords: ['calendar', 'appointments', 'booking', 'meetings', 'calls'] },
      { id: 'approvals', label: 'Approvals', description: 'Agent decisions waiting on you', icon: <ClipboardCheck className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/approvals'), keywords: ['approve', 'reject', 'decisions', 'pending', 'review', 'sign-off'] },
      { id: 'listings', label: 'Listings', description: 'Triage scraped MLS / Zillow / Redfin listings', icon: <Building2 className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/listings'), keywords: ['mls', 'zillow', 'redfin', 'realtor', 'listings', 'feeds', 'scrape'] },
      { id: 'buyers', label: 'Buyers', description: 'Disposition Network', icon: <DollarSign className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/buyers'), keywords: ['dispo', 'cash', 'investors', 'network'], shortcut: '\u23185' },
      { id: 'agents', label: 'Agents', description: 'Real-estate agent network', icon: <Briefcase className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/agents'), keywords: ['realtor', 'broker', 'license'] },
      { id: 'wholesalers', label: 'Wholesalers', description: 'Wholesaler network', icon: <Truck className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/wholesalers'), keywords: ['dispo', 'partners', 'pipeline'] },
      { id: 'ops', label: 'Ops room', description: 'Organization room', icon: <Radio className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/ops'), keywords: ['ops', 'room', 'team', 'ticks'], shortcut: '\u23186' },
      { id: 'settings', label: 'Settings', description: 'Configuration', icon: <Settings className="w-5 h-5" />, category: 'page', onSelect: () => navigate('/settings'), keywords: ['config', 'profile', 'integrations'], shortcut: '\u23187' },
    ]

    const agents: CommandItem[] = [
      { id: 'agent-scout', label: 'Scout', description: 'Leads and markets', icon: <AgentDot role="scout" />, category: 'agent', onSelect: () => navigate(seatPath('scout')), keywords: ['find', 'leads', 'county', 'search'] },
      { id: 'agent-analyst', label: 'Analyst', description: 'Deal analysis', icon: <AgentDot role="analyst" />, category: 'agent', onSelect: () => navigate(seatPath('analyst')), keywords: ['eval', 'arv', 'mao', 'comps', 'analysis'] },
      { id: 'agent-marketer', label: 'Marketer', description: 'Campaigns and outreach', icon: <AgentDot role="marketer" />, category: 'agent', onSelect: () => navigate(seatPath('marketer')), keywords: ['campaign', 'sequence', 'outreach'] },
      { id: 'agent-closer', label: 'Closer', description: 'Offers and contracts', icon: <AgentDot role="closer" />, category: 'agent', onSelect: () => navigate(seatPath('closer')), keywords: ['deal', 'offer', 'contract', 'negotiate'] },
      { id: 'agent-dispo', label: 'Dispo', description: 'Buyer matching', icon: <AgentDot role="dispo" />, category: 'agent', onSelect: () => navigate(seatPath('dispo')), keywords: ['buyer', 'assign', 'match', 'disposition'] },
      { id: 'agent-operator', label: 'Operator', description: 'Ops and reporting', icon: <AgentDot role="operator" />, category: 'agent', onSelect: () => navigate(seatPath('operator')), keywords: ['report', 'kpi', 'status', 'ops'] },
    ]

    const actions: CommandItem[] = [
      { id: 'run-scout', label: 'Run Scout Now', description: 'Trigger a scout scan', icon: <Zap className="w-5 h-5" />, category: 'action', onSelect: () => addToast({ type: 'info', title: 'Scout run triggered', message: 'Scanning target counties...' }), keywords: ['scan', 'autopilot'] },
      { id: 'upload', label: 'Upload Lead List', description: 'Import CSV', icon: <Upload className="w-5 h-5" />, category: 'action', onSelect: () => navigate('/find?upload=true'), keywords: ['csv', 'import', 'list'] },
      { id: 'add-buyer', label: 'Add Buyer', description: 'New cash buyer', icon: <UserPlus className="w-5 h-5" />, category: 'action', onSelect: () => navigate('/buyers?add=true'), keywords: ['cash buyer', 'investor', 'dispo'] },
      { id: 'eval-property', label: 'Evaluate Property', description: 'Run analysis', icon: <BarChart3 className="w-5 h-5" />, category: 'action', onSelect: () => navigate('/eval'), keywords: ['comps', 'arv', 'mao', 'analysis'] },
    ]

    return [...pages, ...agents, ...actions]
  }, [navigate, addToast])

  // -- Filtered results --
  const filtered = useMemo(() => {
    const q = query.trim()
    if (!q) return allItems

    return allItems.filter((item) => {
      const haystack = [item.label, item.description ?? '', ...(item.keywords ?? [])].join(' ')
      return fuzzyMatch(haystack, q)
    })
  }, [query, allItems])

  // -- Dynamic address evaluation item --
  const addressItem = useMemo<CommandItem | null>(() => {
    const q = query.trim()
    if (!looksLikeAddress(q)) return null
    return {
      id: 'eval-address',
      label: `Evaluate "${q}"`,
      description: 'Run property evaluation',
      icon: <BarChart3 className="w-5 h-5" />,
      category: 'action',
      onSelect: () => navigate(`/eval?address=${encodeURIComponent(q)}`),
    }
  }, [query, navigate])

  // -- Final display list --
  const displayItems = useMemo(() => {
    if (addressItem) return [addressItem, ...filtered]
    return filtered
  }, [addressItem, filtered])

  // -- Group by category (preserving order) --
  const grouped = useMemo(() => {
    const order: Array<'page' | 'agent' | 'action'> = ['page', 'agent', 'action']
    const groups: { category: string; items: CommandItem[] }[] = []

    for (const cat of order) {
      const items = displayItems.filter((i) => i.category === cat)
      if (items.length > 0) {
        groups.push({ category: cat, items })
      }
    }
    return groups
  }, [displayItems])

  // -- Flat list for keyboard nav --
  const flatItems = useMemo(() => grouped.flatMap((g) => g.items), [grouped])

  // -- Reset selected index when results change --
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // -- Global keyboard shortcut: Cmd+K / Ctrl+K --
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    const handleOpen = () => setOpen(true)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen)
    }
  }, [])

  // -- Focus input when opened --
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIndex(0)
      // Small delay so the DOM is ready
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // -- Scroll selected item into view --
  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current.querySelector('[data-selected="true"]')
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  const close = useCallback(() => setOpen(false), [])

  const selectItem = useCallback(
    (item: CommandItem) => {
      close()
      item.onSelect()
    },
    [close],
  )

  // -- Keyboard navigation inside the palette --
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((i) => (i + 1) % Math.max(flatItems.length, 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((i) => (i - 1 + flatItems.length) % Math.max(flatItems.length, 1))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const item = flatItems[selectedIndex]
        if (item) selectItem(item)
      }
    },
    [flatItems, selectedIndex, selectItem, close],
  )

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-zinc-800 px-4 py-3">
          <Search className="w-5 h-5 text-zinc-500 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Search pages, properties, actions..."
            className="flex-1 bg-transparent text-lg text-zinc-100 placeholder:text-zinc-500 outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-zinc-700 bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto">
          {flatItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500">
              No results for &quot;{query}&quot;
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.category}>
                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  {categoryLabels[group.category] ?? group.category}
                </div>
                {group.items.map((item) => {
                  const idx = flatItems.indexOf(item)
                  const isSelected = idx === selectedIndex
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-selected={isSelected}
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors',
                        isSelected
                          ? 'bg-zinc-800 ring-1 ring-amber-500/50'
                          : 'hover:bg-zinc-800',
                      )}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-zinc-400 shrink-0">
                        {item.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-zinc-200">{item.label}</span>
                        {item.description && (
                          <span className="ml-2 text-xs text-zinc-500">
                            {item.description}
                          </span>
                        )}
                      </div>
                      {item.shortcut && (
                        <kbd className="text-xs text-zinc-600 shrink-0">
                          {item.shortcut}
                        </kbd>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-4 border-t border-zinc-800 px-4 py-2 text-[11px] text-zinc-600">
          <span><kbd className="font-semibold">&uarr;&darr;</kbd> navigate</span>
          <span><kbd className="font-semibold">&crarr;</kbd> select</span>
          <span><kbd className="font-semibold">esc</kbd> close</span>
        </div>
      </div>
    </div>
  )
}
